/**
 * Created by Rodrigo Quesada on 21/12/16.
 */
import {Observable} from 'rxjs';
import {NgForm, AbstractControl} from '@angular/forms';
import {mapDictionaryEntries, dictionaryValues, dictionaryEntries} from '../../../common/typescript/objects.util';
import {ValidationError} from './custom-validator.class';
import isString = require('lodash/isString');

export type ValidationMessages = {[key: string]: ValidationMessage|string};
export type SpecificValidationMessages = {[field: string]: ValidationMessages|string};

export class FormValidator {

    private readonly invalidControls$: Observable<Observable<{field: string, control: AbstractControl}>>;
    readonly errors$: Observable<FormErrors>;
    readonly dirtyErrors$: Observable<FormErrors>;
    readonly hasErrors$: Observable<boolean>;
    readonly hasCriticalErrors$: Observable<boolean>;

    constructor(form$: Observable<NgForm|undefined>,
                readonly genericValidationMessages: ValidationMessages = {},
                readonly specificValidationMessages$: Observable<SpecificValidationMessages> = Observable.of({})) {

        let presentForm$: Observable<NgForm> = form$.filter(form => form != null);

        let formStatusChange$ = presentForm$.switchMap(form => form.statusChanges.map(() => form).startWith(form));

        this.invalidControls$ = formStatusChange$.map(form =>
            Observable.from(mapDictionaryEntries(form.controls, (k, v) => ({field: k, control: v})))
                .filter(({control}) => control && control.enabled && !control.valid)
        ).publishReplay(1).refCount();

        this.errors$ = this._createErrors$();
        this.dirtyErrors$ = this._createErrors$(true);
        this.hasErrors$ = this.createHasErrors$();
        this.hasCriticalErrors$ = this.createHasErrors$(true);
    }

    private _createErrors$(dirtyOnly = false) {
        return this.invalidControls$.flatMap(controls$ => {

            let errorMessages$ = controls$.filter(({control}) => !dirtyOnly || control.dirty)
                .flatMap(({field, control}) => {

                    let firstError$ = Observable.from(dictionaryEntries(control.errors)).first();

                    return firstError$.withLatestFrom(this.specificValidationMessages$).map(([error, specificValidationMessages]) => {
                        let errorKey = error.key;
                        let errorMessage: string|undefined;

                        let criticalError = error.value instanceof ValidationError ? error.value.critical : true;

                        if (specificValidationMessages[field]) {
                            let currentMessage = specificValidationMessages[field];
                            if (isString(currentMessage)) errorMessage = currentMessage;
                            else errorMessage = FormValidator._validationMessageText(currentMessage[errorKey], criticalError);
                        }
                        if (errorMessage === undefined) errorMessage = FormValidator._validationMessageText(this.genericValidationMessages[errorKey], criticalError);
                        if (errorMessage === undefined) errorMessage = error.value instanceof ValidationError ? error.value.message : error.key;

                        return {field: field, message: errorMessage};
                    });
                });

            return errorMessages$.reduce((formErrors, error) => {
                formErrors[error.field] = error.message;
                return formErrors;
            }, {});
        }).publishReplay(1).refCount();
    }

    private createHasErrors$(criticalOnly = false) {
        return this.invalidControls$.flatMap(controls$ =>
            controls$.flatMap(({control}) => Observable.from(dictionaryValues(control.errors)))
                .first(error => !criticalOnly || error instanceof ValidationError && error.critical, () => true, false)
        ).distinctUntilChanged().publishReplay(1).refCount();
    }

    // region Utils
    private static _validationMessageText(validationMessage: string|ValidationMessage|undefined, critical: boolean) {
        return validationMessage instanceof ValidationMessage ? validationMessage.getMessage(critical) : validationMessage;
    }

    // endregion
}

export class ValidationMessage {
    constructor(readonly critical: string,
                readonly nonCritical: string) {
    }

    getMessage(isCritical: boolean) {
        return isCritical ? this.critical : this.nonCritical;
    }
}

export type FormErrors = {[key: string]: string};



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/forms/validation/form-validator.class.ts